import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type {
  AssetInventoryExecutiveAsset,
  AssetInventoryExecutiveSummary,
} from '../../services/enterpriseOperationalUxApi'
import { formatRelativeTime } from '../../utils/operationalUx'
import { mapAssetToUnifiedContext } from '../../utils/unifiedExecutiveContextMappers'
import ExecutiveOperationalContextPanel from './context/ExecutiveOperationalContextPanel'
import OperationalBlastRadiusPanel from './context/OperationalBlastRadiusPanel'
import OperationalRuntimeTimeline from './context/OperationalRuntimeTimeline'
import styles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

export type AssetInventorySelection =
  | { kind: 'asset'; asset: AssetInventoryExecutiveAsset }
  | { kind: 'vulnerability'; asset: AssetInventoryExecutiveAsset; vulnerabilityId: string; title: string }
  | { kind: 'runtime'; event: AssetInventoryExecutiveSummary['runtimeEvents'][number] }
  | { kind: 'blast'; impact: AssetInventoryExecutiveSummary['blastRadiusImpacts'][number] }
  | { kind: 'exposure'; asset: AssetInventoryExecutiveAsset }
  | null

type Props = {
  selection: AssetInventorySelection
  onClose?: () => void
}

export default function AssetInventoryExecutiveContextPanel({ selection, onClose }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()

  if (!selection) {
    return (
      <div className={styles.aiContextEmpty}>
        <p>{t('enterpriseOperationalUx.assetInventory.selectAsset', 'Select an asset, vulnerability, exposure issue, or operational event.')}</p>
      </div>
    )
  }

  const header = (title: string) => (
    <header className={styles.aiContextHeader}>
      <h3>{title}</h3>
      {onClose ? (
        <button type="button" className={styles.aiContextClose} onClick={onClose}>
          {t('common.close', 'Close')}
        </button>
      ) : null}
    </header>
  )

  if (selection.kind === 'runtime') {
    const ev = selection.event
    return (
      <div className={styles.aiContextPanel}>
        {header(t('enterpriseOperationalUx.assetInventory.runtimeDegradation', 'Runtime degradation'))}
        <OperationalRuntimeTimeline
          events={[
            {
              id: ev.id,
              at: ev.at,
              eventType: ev.eventType,
              severity: ev.severity || 'INFO',
              summary: ev.summary,
            },
          ]}
          mode="full"
          maxItems={1}
        />
        <p className={styles.aiContextMeta}>{formatRelativeTime(ev.at)}</p>
      </div>
    )
  }

  if (selection.kind === 'blast') {
    const imp = selection.impact
    return (
      <div className={styles.aiContextPanel}>
        {header(t('enterpriseOperationalUx.layerBlast', 'Shared control blast radius'))}
        <OperationalBlastRadiusPanel
          hits={[
            {
              controlCode: imp.controlCode,
              summary: imp.summary,
              impactedStandards: imp.impactedStandards || [],
              standardCount: imp.standardCount,
              systemCount: imp.systemCount,
              severity: 'MODERATE',
            },
          ]}
          mode="expanded"
        />
      </div>
    )
  }

  const asset = selection.kind === 'asset' || selection.kind === 'exposure' ? selection.asset : selection.asset
  const context = mapAssetToUnifiedContext(asset)
  const sev = asset.vulnerability.severity
  const vulnLine =
    asset.vulnerability.count > 0
      ? t(
          'enterpriseOperationalUx.assetInventory.vulnBreakdownLine',
          '{{total}} current findings · C:{{critical}} H:{{high}} M:{{medium}} L:{{low}} I:{{info}}',
          {
            total: asset.vulnerability.count,
            critical: sev?.critical ?? 0,
            high: sev?.high ?? 0,
            medium: sev?.medium ?? 0,
            low: sev?.low ?? 0,
            info: sev?.info ?? 0,
          },
        )
      : t('enterpriseOperationalUx.assetInventory.noCurrentFindings', 'No open vulnerability findings on this asset.')

  return (
    <div className={`${styles.aiContextPanel} ${styles.aiContextPanelEnterprise}`}>
      {header(asset.name)}
      <p className={styles.aiContextVulnLine}>{vulnLine}</p>
      <ExecutiveOperationalContextPanel
        context={context}
        mode="compact"
        showHeader={false}
        dark
        sections={{ stability: false, metrics: true }}
        footerActions={
          asset.workflow.stages[0] ? (
            <button
              type="button"
              className={styles.aiWorkflowChip}
              onClick={() =>
                navigate(`/compliance-workflow/${encodeURIComponent(asset.workflow.stages[0].standardCode)}`)
              }
            >
              {t('enterpriseOperationalUx.openWorkflow', 'Open workflow')}
            </button>
          ) : null
        }
      />
    </div>
  )
}
